// @ts-strict-ignore
import { useApolloClient } from "@apollo/client";
import { handleNestedMutationErrors } from "@dashboard/auth";
import { WindowTitle } from "@dashboard/components/WindowTitle";
import {
  OrderCustomerNotificationTypeEnum,
  OrderDetailsWithMetadataDocument,
  useFulfillOrderMutation,
  useOrderFulfillDataQuery,
  useOrderFulfillSettingsQuery,
  useOrderSendNotificationMutation,
  useUpdateMetadataMutation,
  useUpdatePrivateMetadataMutation,
} from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import { getMutationErrors } from "@dashboard/misc";
import OrderFulfillPage, {
  OrderFulfillSubmitData,
} from "@dashboard/orders/components/OrderFulfillPage";
import {
  orderFulfillUrl,
  OrderFulfillUrlDialog,
  OrderFulfillUrlQueryParams,
  orderUrl,
} from "@dashboard/orders/urls";
import {
  generatePickupPin,
  getPickupPin,
  isParcelShopDelivery,
  isStorePickup,
  PICKUP_PIN_KEY,
  PICKUP_READY_NOTIFIED_KEY,
} from "@dashboard/orders/utils/delivery";
import createDialogActionHandlers from "@dashboard/utils/handlers/dialogActionHandlers";
import { useIntl } from "react-intl";

interface OrderFulfillProps {
  orderId: string;
  params: OrderFulfillUrlQueryParams;
}

const OrderFulfill = ({ orderId, params }: OrderFulfillProps) => {
  const apolloClient = useApolloClient();
  const navigate = useNavigator();
  const notify = useNotifier();
  const intl = useIntl();
  const [openModal, closeModal] = createDialogActionHandlers<
    OrderFulfillUrlDialog,
    OrderFulfillUrlQueryParams
  >(navigate, params => orderFulfillUrl(orderId, params), params);
  const { data: settings, loading: settingsLoading } = useOrderFulfillSettingsQuery({});
  const { data, loading } = useOrderFulfillDataQuery({
    displayLoader: true,
    variables: {
      orderId,
    },
  });
  const isPickup = isStorePickup(data?.order?.deliveryType);
  const [updateMetadata] = useUpdateMetadataMutation({});
  const [updatePrivateMetadata] = useUpdatePrivateMetadataMutation({});
  const [orderSendNotification] = useOrderSendNotificationMutation({});
  // In-store pickup: preparing the order can also move it to "Ready for pickup"
  // (same metadata flag as the order page's toggle) and, optionally, email the
  // customer that it can be collected.
  const markReadyForPickup = async (notifyCustomer: boolean) => {
    if (!getPickupPin(data?.order?.privateMetadata)) {
      await updatePrivateMetadata({
        variables: {
          id: orderId,
          input: [{ key: PICKUP_PIN_KEY, value: generatePickupPin() }],
          keysToDelete: [],
        },
      });
    }

    await updateMetadata({
      variables: {
        id: orderId,
        input: [{ key: PICKUP_READY_NOTIFIED_KEY, value: "true" }],
        keysToDelete: [],
      },
    });

    if (notifyCustomer) {
      await orderSendNotification({
        variables: {
          id: orderId,
          notificationType: OrderCustomerNotificationTypeEnum.READY_FOR_PICKUP,
        },
      });
    }

    await apolloClient.refetchQueries({ include: [OrderDetailsWithMetadataDocument] });
  };
  const [fulfillOrder, fulfillOrderOpts] = useFulfillOrderMutation({
    onCompleted: data => {
      if (data.orderFulfill.errors.length === 0) {
        navigate(orderUrl(orderId), { replace: true });
        notify({
          status: "success",
          text: intl.formatMessage({
            id: "CYEnGq",
            defaultMessage: "Fulfilled Items",
            description: "order fulfilled success message",
          }),
        });
      } else {
        if (!data.orderFulfill.errors.every(err => err.code === "INSUFFICIENT_STOCK")) {
          handleNestedMutationErrors({ data, intl, notify });
        }
      }
    },
    disableErrorHandling: true,
  });

  return (
    <>
      <WindowTitle
        title={
          data?.order?.number
            ? intl.formatMessage(
                {
                  id: "2MKBk2",
                  defaultMessage: "Fulfill Order #{orderNumber}",
                  description: "window title",
                },
                {
                  orderNumber: data.order.number,
                },
              )
            : intl.formatMessage({
                id: "NzifUg",
                defaultMessage: "Fulfill Order",
                description: "window title",
              })
        }
      />
      <OrderFulfillPage
        params={params}
        loading={loading || settingsLoading || fulfillOrderOpts.loading}
        errors={fulfillOrderOpts.data?.orderFulfill.errors}
        onSubmit={async (formData: OrderFulfillSubmitData) => {
          const res = await fulfillOrder({
            variables: {
              input: {
                lines: formData.items
                  .filter(line => !!line?.value)
                  .map(line => ({
                    orderLineId: line.id,
                    stocks: line.value,
                  })),
                notifyCustomer:
                  !isPickup && settings?.shop?.fulfillmentAutoApprove && formData.sendInfo,
                allowStockToBeExceeded: formData.allowStockToBeExceeded,
                trackingNumber: isPickup ? undefined : formData.trackingNumber,
                ...(isParcelShopDelivery(data?.order?.deliveryType) && {
                  pontoRecolhaAddress: formData.pontoRecolhaAddress.trim(),
                }),
              },
              orderId,
            },
          });

          const errors = getMutationErrors(res);

          if (isPickup && formData.markReadyForPickup && !errors.length) {
            await markReadyForPickup(formData.notifyReadyForPickup);
          }

          return errors;
        }}
        order={data?.order}
        saveButtonBar={fulfillOrderOpts.status}
        shopSettings={settings?.shop}
        openModal={openModal}
        closeModal={closeModal}
      />
    </>
  );
};

OrderFulfill.displayName = "OrderFulfill";
export default OrderFulfill;
